/**
 * usePreSaveApply — the drawer's CUSTOM Apply: save a dirty Standard draft to
 * the tracker first, then run the optimizer apply.
 * For Media Devoted | Created By Ciro Autiero
 *
 * The optimizer re-reads the LIVE flow, so unsaved Standard-tab edits (e.g. a
 * hand-set weight on a rule-excluded / frozen path) would be ignored and the
 * excluded weight reverted. Persisting the draft FIRST makes Apply honor those
 * manual weights — writing the same result the live preview already showed.
 * The draft goes to the drawer's OWN tracker (Voluum or Tres Commas).
 * A tracker whose Apply can be held (Tres Commas) decides on its FULL hold
 * state (useTrackerHold — the global switch included): while held, nothing is
 * written and the apply answers "nothing pushed" with the reason; until that
 * state is known, Apply waits (`pending`), so a half-known Apply never writes.
 * Split out of FlowOptimizerLivePreviewContext (over the file limit).
 */

import { useCallback, useMemo, useState } from 'react';
import { trackerLabel, type TrackerId, type VoluumFlowUpdate } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/api-error';
import { useToast } from '@/providers/ToastProvider';
import { rawFlowApiOf, type RawFlowApi } from '@/features/flow-optimizer/services/raw-flow/raw-flow-api';
import { drawerProfile } from '@/features/flow-optimizer/tracker';
import { useTrackerHold } from '@/features/trackers/flow-optimizer';

export interface UsePreSaveApplyParams {
  provider: TrackerId;
  flowId: string;
  /** The dirty Standard draft, or null when there is nothing unsaved. */
  dirtyDraft: VoluumFlowUpdate | null;
  runApply: () => void;
}

export interface PreSaveApply {
  onApply: () => void;
  /** Saving the draft, or waiting for the tracker's hold state: Apply is busy. */
  pending: boolean;
}

interface SaveThenApply {
  rawApi: RawFlowApi;
  flowId: string;
  draft: VoluumFlowUpdate;
  runApply: () => void;
}

/** Writes the draft, then applies; a failed write rejects and never applies. */
function saveThenApply({ rawApi, flowId, draft, runApply }: SaveThenApply): Promise<void> {
  return rawApi.update(flowId, draft).then(() => runApply());
}

/** Where the draft is saved first (null: apply straight away), and whether
 *  Apply must wait for the tracker's hold state. */
function usePreSaveTarget(provider: TrackerId) {
  const hold = useTrackerHold(provider);
  const waiting = drawerProfile(provider).reportsUnpushed && !hold.known;
  const rawApi = useMemo(
    () => (drawerProfile(provider).preSaveRawDraft && !hold.reason ? rawFlowApiOf(provider) : null),
    [provider, hold.reason],
  );
  return { rawApi, waiting };
}

export function usePreSaveApply(params: UsePreSaveApplyParams): PreSaveApply {
  const { provider, flowId, dirtyDraft, runApply } = params;
  const toast = useToast();
  // True while the pre-apply draft save is in flight — folded into `pending`
  // so the Apply button is disabled during that window too (otherwise a fast
  // second click would fire a second overlapping apply).
  const [saving, setSaving] = useState(false);
  const { rawApi, waiting } = usePreSaveTarget(provider);

  const onApply = useCallback(() => {
    if (waiting || saving) return;
    if (!rawApi || !dirtyDraft) return runApply();
    setSaving(true);
    const fallback = `Could not save your edits to ${trackerLabel(provider)}.`;
    void saveThenApply({ rawApi, flowId, draft: dirtyDraft, runApply })
      .catch((err) => toast.error('Apply failed', getApiErrorMessage(err, fallback)))
      .finally(() => setSaving(false));
  }, [waiting, saving, rawApi, dirtyDraft, runApply, flowId, toast, provider]);

  return { onApply, pending: saving || waiting };
}
